import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import type {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  User,
  UserCreate,
  UserUpdate,
  UserUpdateAdmin,
  PasswordUpdate,
  GenerateTitlesRequest,
  GenerateTitlesResponse,
  WriteBlogRequest,
  BlogJobResponse,
  BlogJobStatusResponse,
  BatchWriteRequest,
  BatchWriteResponse,
  JobBatchSummary,
  JobBatchDetail,
  KeywordsResponse,
  ApproveJobsRequest,
  ApproveJobsResponse,
  PaginatedResponse,
  PublishToMediumRequest,
  PublishToMediumResponse,
  HealthCheck,
  ClaudeStatus,
  ClaudeCredentialsResponse,
  ClaudeValidateResponse,
  CreateTitleRunRequest,
  RegenerateTitlesRequest,
  TitleRun,
  TitleRunListItem,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;
      if (refreshToken) {
        try {
          const response = await axios.post<TokenResponse>(
            `${API_BASE_URL}/api/v1/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token, refresh_token } = response.data;
          useAuthStore.getState().setTokens(access_token, refresh_token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          useAuthStore.getState().logout();
          window.location.replace('/login');
          return Promise.reject(refreshError);
        }
      } else {
        useAuthStore.getState().logout();
        window.location.replace('/login');
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<TokenResponse> => {
    const response = await api.post<TokenResponse>('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<User> => {
    const response = await api.post<User>('/auth/register', data);
    return response.data;
  },

  refresh: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await api.post<TokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  getMe: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },
};

// Users API
export const usersApi = {
  getAll: async (page = 1, page_size = 10): Promise<PaginatedResponse<User>> => {
    const response = await api.get<PaginatedResponse<User>>('/users', {
      params: { page, page_size },
    });
    return response.data;
  },

  getById: async (userId: number): Promise<User> => {
    const response = await api.get<User>(`/users/${userId}`);
    return response.data;
  },

  create: async (data: UserCreate): Promise<User> => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },

  update: async (userId: number, data: UserUpdateAdmin): Promise<User> => {
    const response = await api.put<User>(`/users/${userId}`, data);
    return response.data;
  },

  delete: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  updateProfile: async (data: UserUpdate): Promise<User> => {
    const response = await api.put<User>('/users/me/profile', data);
    return response.data;
  },

  updatePassword: async (data: PasswordUpdate): Promise<void> => {
    await api.put('/users/me/password', data);
  },
};

// Blogs API
export const blogsApi = {
  generateTitles: async (data: GenerateTitlesRequest): Promise<GenerateTitlesResponse> => {
    const response = await api.post<GenerateTitlesResponse>('/blogs/generate-titles', data);
    return response.data;
  },

  writeBlog: async (data: WriteBlogRequest): Promise<BlogJobResponse> => {
    const response = await api.post<BlogJobResponse>('/blogs/write', data);
    return response.data;
  },

  getJobStatus: async (jobId: number): Promise<BlogJobStatusResponse> => {
    const response = await api.get<BlogJobStatusResponse>(`/blogs/jobs/${jobId}`);
    return response.data;
  },

  getJobs: async (page = 1, page_size = 10, keywords?: string[]): Promise<PaginatedResponse<BlogJobStatusResponse>> => {
    const params: Record<string, unknown> = { page, page_size };
    if (keywords && keywords.length > 0) {
      params.keywords = keywords.join(',');
    }
    const response = await api.get<PaginatedResponse<BlogJobStatusResponse>>('/blogs/jobs', { params });
    return response.data;
  },

  getKeywords: async (): Promise<KeywordsResponse> => {
    const response = await api.get<KeywordsResponse>('/blogs/keywords');
    return response.data;
  },

  approve: async (data: ApproveJobsRequest): Promise<ApproveJobsResponse> => {
    const response = await api.post<ApproveJobsResponse>('/blogs/approve', data);
    return response.data;
  },

  batchWrite: async (data: BatchWriteRequest): Promise<BatchWriteResponse> => {
    const response = await api.post<BatchWriteResponse>('/blogs/batches/write', data);
    return response.data;
  },

  getBatches: async (page = 1, page_size = 10): Promise<PaginatedResponse<JobBatchSummary>> => {
    const response = await api.get<PaginatedResponse<JobBatchSummary>>('/blogs/batches', {
      params: { page, page_size },
    });
    return response.data;
  },

  getBatchDetail: async (batchId: number): Promise<JobBatchDetail> => {
    const response = await api.get<JobBatchDetail>(`/blogs/batches/${batchId}`);
    return response.data;
  },

  publishToMedium: async (blogId: number, data: PublishToMediumRequest): Promise<PublishToMediumResponse> => {
    const response = await api.post<PublishToMediumResponse>(`/blogs/${blogId}/publish/medium`, data);
    return response.data;
  },

  deleteBlog: async (blogId: number): Promise<void> => {
    await api.delete(`/blogs/${blogId}`);
  },
};

// Health API
export const healthApi = {
  check: async (): Promise<HealthCheck> => {
    const response = await api.get<HealthCheck>('/health');
    return response.data;
  },
};

// Claude Connection API (admin only)
export const claudeApi = {
  getStatus: async (): Promise<ClaudeStatus> => {
    const response = await api.get<ClaudeStatus>('/claude/status');
    return response.data;
  },

  uploadCredentials: async (credentials: string): Promise<ClaudeCredentialsResponse> => {
    const response = await api.post<ClaudeCredentialsResponse>('/claude/credentials', {
      credentials,
    });
    return response.data;
  },

  validate: async (): Promise<ClaudeValidateResponse> => {
    const response = await api.post<ClaudeValidateResponse>('/claude/validate');
    return response.data;
  },

  deleteCredentials: async (): Promise<ClaudeCredentialsResponse> => {
    const response = await api.delete<ClaudeCredentialsResponse>('/claude/credentials');
    return response.data;
  },
};

// Title Runs API — async title generation with polling
export const titlesApi = {
  createRun: async (data: CreateTitleRunRequest): Promise<TitleRun> => {
    const response = await api.post<TitleRun>('/titles/runs', data);
    return response.data;
  },

  getRun: async (runId: number): Promise<TitleRun> => {
    const response = await api.get<TitleRun>(`/titles/runs/${runId}`);
    return response.data;
  },

  regenerate: async (runId: number, data: RegenerateTitlesRequest): Promise<TitleRun> => {
    const response = await api.post<TitleRun>(`/titles/runs/${runId}/regenerate`, data);
    return response.data;
  },

  getRuns: async (page = 1, page_size = 10): Promise<PaginatedResponse<TitleRunListItem>> => {
    const response = await api.get<PaginatedResponse<TitleRunListItem>>('/titles/runs', {
      params: { page, page_size },
    });
    return response.data;
  },
};

export default api;
